/**
 * Public KYC upload API: the secret in the WhatsApp link is the key.
 *   GET  {BASE}/api/v1/kyc/public/:token
 *   GET  {BASE}/api/v1/kyc/public/:token/pin?pin=
 *   POST {BASE}/api/v1/kyc/public/:token   (multipart: iqama.*, r<i>.*, photos, applyAll)
 */

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

export type Phone = { code: string; number: string };

export type KycInfo = {
  state: 'open' | 'submitted' | 'expired';
  awbNumber: string;
  boxes: number;
  boxNumbers: string[];
  senderName: string | null;
  receiverName: string | null;
  iqama?: { name: string | null; address: string | null; mobile: Phone };
  aadhaar?: {
    name: string | null;
    address: string | null;
    mobile: Phone;
    pin: string | null;
    district: string | null;
    state: string | null;
    country: string | null;
  };
};

export type PinInfo = { district: string | null; state: string | null; country: string | null; localities: string[] };

export type KycSide = {
  number?: string;
  name?: string;
  address?: string;
  area?: string;
  mobileCode?: string;
  mobileNumber?: string;
  pin?: string;
  district?: string;
  state?: string;
  country?: string;
  front?: Blob | null;
  back?: Blob | null;
};

/** A box's receiver: their Aadhaar, or another ID (passport…) when they live outside India. */
export type KycReceiver = KycSide & { kind: 'aadhaar' | 'other'; docName?: string };

export class KycError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/api/v1/kyc/public/${path}`, init);
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const m = json?.message;
    throw new KycError((Array.isArray(m) ? m[0] : m) ?? 'Something went wrong', res.status);
  }
  return (json?.data ?? json) as T;
}

export const getKycInfo = (token: string) => call<KycInfo>(encodeURIComponent(token));

export const lookupPin = (token: string, pin: string) =>
  call<PinInfo>(`${encodeURIComponent(token)}/pin?pin=${encodeURIComponent(pin)}`);

const TEXT = ['kind', 'docName', 'number', 'name', 'address', 'area', 'mobileCode', 'mobileNumber', 'pin', 'district', 'state', 'country'] as const;

/** The sender's Iqama and one receiver per box (r0, r1…) — or only r0 with applyAll, for every box. */
export const submitKyc = (token: string, iqama: KycSide, receivers: KycReceiver[], applyAll: boolean) => {
  const body = new FormData();
  const add = (key: string, v: KycSide | KycReceiver) => {
    for (const k of TEXT) {
      const value = (v as Record<string, unknown>)[k];
      if (typeof value === 'string' && value) body.append(`${key}.${k}`, value);
    }
    if (v.front) body.append(`${key}Front`, v.front, `${key}-front.jpg`);
    if (v.back) body.append(`${key}Back`, v.back, `${key}-back.jpg`);
  };
  add('iqama', iqama);
  (applyAll ? receivers.slice(0, 1) : receivers).forEach((r, i) => add(`r${i}`, r));
  if (applyAll) body.append('applyAll', '1');
  return call<{ state: 'submitted'; awbNumber: string }>(encodeURIComponent(token), { method: 'POST', body });
};
